// src/utils/validatePhone.js

/**
 * Phone Number Validator
 * -----------------------
 * Validates a phone number broken into four parts:
 *   - `intl`: International code (e.g., "+1")
 *   - `area`: Area code (3 digits)
 *   - `prefix`: Prefix code (3 digits)
 *   - `line`: Line number (4 digits)
 *
 * This function ensures formatting is correct and all sections have the proper digit lengths.
 * Returns a full normalized number (e.g., +15551234567), validation status, and error messages.
 *
 * Intended for use in registration or contact forms.
 */

/**
 * Validates and formats a phone number input.
 *
 * @param {Object} param0
 * @param {string} param0.intl - International code (e.g., "+1")
 * @param {string} param0.area - Area code (e.g., "555")
 * @param {string} param0.prefix - Prefix (e.g., "123")
 * @param {string} param0.line - Line number (e.g., "4567")
 * @returns {{
*   isValid: boolean,
*   fullNumber: string,
*   errors: string[]
* }}
*/
export function validatePhone({ intl, area, prefix, line }) {
 const errors = [];

 // Helper: strips all non-digit characters
 const digitsOnly = (str) => str.replace(/\D/g, "");

 // Normalize each segment to digits only
 const intlCode = digitsOnly(intl);
 const areaCode = digitsOnly(area);
 const prefixCode = digitsOnly(prefix);
 const lineCode = digitsOnly(line);

 // ---------------------------------------------------------------------
 // Validation Checks
 // ---------------------------------------------------------------------

 // International code must begin with "+"
 if (!intl.startsWith("+")) {
   errors.push("International code must start with +");
 }

 // International code should be 1–3 digits (typical for most countries)
 if (intlCode.length < 1 || intlCode.length > 3) {
   errors.push("Invalid international code length");
 }

 // Area code should be exactly 3 digits
 if (areaCode.length !== 3) {
   errors.push("Area code must be 3 digits");
 }

 // Prefix should be exactly 3 digits
 if (prefixCode.length !== 3) {
   errors.push("Prefix must be 3 digits");
 }

 // Line number should be exactly 4 digits
 if (lineCode.length !== 4) {
   errors.push("Line number must be 4 digits");
 }

 // ---------------------------------------------------------------------
 // Final Output
 // ---------------------------------------------------------------------
 const isValid = errors.length === 0;

 // Construct full E.164-style phone number: +15551234567
 const fullNumber = `+${intlCode}${areaCode}${prefixCode}${lineCode}`;

 return {
   isValid,
   fullNumber,
   errors,
 };
}